import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Plus } from "lucide-react";

import { Badge, Button, EmptyState, ErrorState, SkeletonRows, Status, Surface, Table, TableContainer, TBody, Td, Th, THead, Tr, cn, focusRing, rowOpens } from "../../../../components/ui";
import { businessApi } from "../../../../services/api";
import ScheduleFormDialog from "./ScheduleFormDialog";
import { schedulePath } from "./hrPayroll";
import { CADENCE, STATUS, dayText, nextWindow, ruleText } from "./payScheduleView";

/**
 * HR › Payroll › Pay schedules (Payroll Gate 3). A business has a handful of schedules, so this is a small
 * contextual list (DESIGN.md › Data collections, class E): natural order (active, then drafts, then archived), no
 * search or sort. Archived schedules are behind one disclosure because they pay nobody.
 */
export default function PaySchedulesList() {
  const { canManage, setNotice, reload } = useOutletContext();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: false, data: null });
  const [attempt, setAttempt] = useState(0);
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    let live = true;
    setState((current) => ({ ...current, loading: true, error: false }));
    businessApi.getPaySchedules()
      .then((res) => { if (live) setState({ loading: false, error: false, data: res?.data || { schedules: [] } }); })
      .catch(() => { if (live) setState({ loading: false, error: true, data: null }); });
    return () => { live = false; };
  }, [attempt]);

  if (state.loading && !state.data) return <SkeletonRows rows={3} columns={4} label="Loading pay schedules" />;
  if (state.error) return <Surface><ErrorState title="Pay schedules couldn't be loaded" onRetry={() => setAttempt((n) => n + 1)} /></Surface>;

  const { schedules = [], today } = state.data;
  const current = schedules.filter((row) => row.status !== "archived");
  const archived = schedules.filter((row) => row.status === "archived");
  const shown = showArchived ? [...current, ...archived] : current;

  const header = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="max-w-[75ch] text-body-sm text-secondary">
        A pay schedule decides how often payroll runs and when each period is paid. Each employee is on one pay schedule;
        how their salary is stated (per month, per day) is set on their Pay.
      </p>
      {canManage ? <Button icon={Plus} onClick={() => setCreating(true)}>New pay schedule</Button> : null}
    </div>
  );

  return (
    <div className="space-y-4">
      {header}
      <Surface>
        {!shown.length ? (
          <EmptyState title="No pay schedule yet" description="Draft one; the business owner activates it before anyone is paid on it." />
        ) : (
          <>
            {/* Desktop: a table. Phones: condensed rows (DESIGN.md › Mobile composition). */}
            <div className="max-md:hidden">
              <TableContainer label="Pay schedules">
                <Table>
                  <THead>
                    <Tr>
                      <Th>Pay schedule</Th>
                      <Th>Status</Th>
                      <Th>How often</Th>
                      <Th numeric>Employees today</Th>
                      <Th>Next cutoff</Th>
                      <Th>Next pay date</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {shown.map((row) => {
                      const next = row.status === "active" ? nextWindow(row, today) : null;
                      return (
                        <Tr key={row.id} {...rowOpens(() => navigate(schedulePath(row.id)))}>
                          <Td>
                            <Link to={schedulePath(row.id)} className={cn("rounded-control font-medium text-foreground hover:underline", focusRing)}>{row.name}</Link>
                            {row.isDefault ? <span className="ml-2"><Badge>New employees start here</Badge></span> : null}
                            <p className="text-meta text-secondary">{ruleText(row)}</p>
                          </Td>
                          <Td><Status tone={STATUS[row.status]?.tone}>{STATUS[row.status]?.label || row.status}</Status></Td>
                          <Td>{CADENCE[row.frequency] || row.frequency}</Td>
                          <Td numeric>{row.assignedToday}</Td>
                          <Td>{next ? dayText(next.endDate) : "—"}</Td>
                          <Td>{next ? dayText(next.payDate) : row.status === "draft" ? "Not active yet" : "—"}</Td>
                        </Tr>
                      );
                    })}
                  </TBody>
                </Table>
              </TableContainer>
            </div>
            <ul className="divide-y divide-subtle md:hidden" aria-label="Pay schedules">
              {shown.map((row) => {
                const next = row.status === "active" ? nextWindow(row, today) : null;
                return (
                  <li key={row.id}>
                    <Link to={schedulePath(row.id)} className={cn("flex min-h-touch flex-col gap-0.5 px-4 py-3", focusRing)}>
                      <span className="flex items-center justify-between gap-3">
                        <span className="font-medium text-foreground">{row.name}</span>
                        <Status tone={STATUS[row.status]?.tone}>{STATUS[row.status]?.label}</Status>
                      </span>
                      <span className="text-meta text-secondary">{CADENCE[row.frequency]} · {row.assignedToday} employee{row.assignedToday === 1 ? "" : "s"}{row.isDefault ? " · new employees start here" : ""}</span>
                      {next ? <span className="text-meta text-secondary">Next pay date {dayText(next.payDate)}</span> : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
        {archived.length ? (
          <div className="border-t border-subtle px-4 py-2">
            <Button size="sm" variant="quiet" aria-expanded={showArchived} onClick={() => setShowArchived((value) => !value)}>
              {showArchived ? "Hide archived" : `Show archived (${archived.length})`}
            </Button>
          </div>
        ) : null}
      </Surface>

      {creating ? (
        <ScheduleFormDialog
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={(saved) => {
            setCreating(false);
            reload();
            setNotice({ tone: "success", text: `Draft "${saved.name}" saved. The business owner activates it before anyone is paid on it.` });
            navigate(schedulePath(saved.id));
          }}
        />
      ) : null}
    </div>
  );
}
